import { formatInTimeZone } from 'date-fns-tz';
import { es } from 'date-fns/locale';

import {
  MONTH_OPTIONS,
  ORDINAL_OPTIONS,
  ORDINAL_WEEKDAY_OPTIONS,
  REPEAT_PRESET_OPTIONS,
  WEEKDAY_OPTIONS,
} from '../constants';
import {
  CustomRecurrenceFields,
  OrdinalWeekdayRule,
  RepeatPreset,
  ScheduleDateOrRelative,
} from '../types';
import { parseCronExpression } from './cron';
import { fromAbsoluteDateString } from './date-time';
import { describeIncrements, isImmediate, resolveRelativeDate } from './relative-date';

const DATE_TIME_LABEL_FORMAT = "d 'de' MMMM 'de' yyyy, HH:mm";

/** Human-readable description of a scheduled/recurring date bound, resolved against `now`. */
export function describeDateOrRelative(
  value: ScheduleDateOrRelative | undefined,
  timeZone: string,
  bound: 'start' | 'end' = 'start',
  relativeToTrigger = false,
): string {
  if (!value) return 'Sin definir';

  if (typeof value === 'string') {
    const date = fromAbsoluteDateString(value, timeZone);
    return date ? formatInTimeZone(date, timeZone, DATE_TIME_LABEL_FORMAT, { locale: es }) : value;
  }

  if (relativeToTrigger) {
    if (isImmediate(value)) return 'Después de un accionador';
    return `${describeIncrements(value.increments)} después del accionador`;
  }

  if (value.reference === 'previous') {
    if (isImmediate(value)) return 'Al finalizar la anterior';
    const incrementsLabel = describeIncrements(value.increments);
    return `${incrementsLabel} ${bound === 'end' ? 'después' : 'después de finalizar la anterior'}`;
  }

  if (isImmediate(value)) return 'Ahora';

  const resolved = resolveRelativeDate(value);
  const incrementsLabel = describeIncrements(value.increments);
  return `${formatInTimeZone(resolved, timeZone, DATE_TIME_LABEL_FORMAT, { locale: es })} (${incrementsLabel} después de ahora)`;
}

function weekdayLabel(code: string): string {
  return WEEKDAY_OPTIONS.find((option) => option.value === code)?.fullLabel ?? code;
}

function monthLabel(month: number): string {
  return (
    MONTH_OPTIONS.find((option) => option.value === month)?.label.toLowerCase() ?? String(month)
  );
}

function joinWithY(items: string[]): string {
  if (items.length === 0) return '';
  if (items.length === 1) return items[0];
  return `${items.slice(0, -1).join(', ')} y ${items[items.length - 1]}`;
}

/** e.g. "1er. miércoles", "3er. día de fin de semana", "último día". */
function describeOrdinalRule(rule: OrdinalWeekdayRule): string {
  const abbreviation =
    ORDINAL_OPTIONS.find((option) => option.value === rule.ordinal)?.abbreviation ?? '';
  const weekdayText =
    ORDINAL_WEEKDAY_OPTIONS.find((option) => option.value === rule.weekday)?.label.toLowerCase() ??
    rule.weekday;
  return `${abbreviation} ${weekdayText}`;
}

/** "de cada mes"/"de cada semana"/"de cada año" for interval 1, otherwise "cada N meses/semanas/años". */
function describeIntervalSuffix(interval: number, singular: string, plural: string): string {
  return interval === 1 ? `de cada ${singular}` : `cada ${interval} ${plural}`;
}

/** e.g. "1o., 3 y 5" — only the day "1" gets an ordinal-like suffix, matching the calendar day picker. */
function describeDaysOfMonth(days: number[]): string {
  const sorted = [...days].sort((a, b) => a - b);
  return joinWithY(sorted.map((day) => (day === 1 ? '1o.' : String(day))));
}

function describeCustomRecurrence(custom: CustomRecurrenceFields): string {
  const { interval } = custom;

  if (custom.frequency === 'minutely') {
    return interval === 1 ? 'Se repetirá cada minuto.' : `Se repetirá cada ${interval} minutos.`;
  }

  if (custom.frequency === 'hourly') {
    return interval === 1 ? 'Se repetirá cada hora.' : `Se repetirá cada ${interval} horas.`;
  }

  if (custom.frequency === 'daily') {
    return interval === 1 ? 'Se repetirá cada día.' : `Se repetirá cada ${interval} días.`;
  }

  // no step operator exists for day-of-week, so `interval` doesn't apply here
  if (custom.frequency === 'weekly') {
    const days = joinWithY(custom.weekdays.map(weekdayLabel).map((day) => day.toLowerCase()));
    return `Se repetirá el ${days} de cada semana.`;
  }

  if (custom.frequency === 'monthly') {
    if (custom.monthly.mode === 'ordinal') {
      return `Se repetirá el ${describeOrdinalRule(custom.monthly.ordinal)} ${describeIntervalSuffix(interval, 'mes', 'meses')}.`;
    }
    return `Se repetirá el ${describeDaysOfMonth(custom.monthly.daysOfMonth)} ${describeIntervalSuffix(interval, 'mes', 'meses')}.`;
  }

  // yearly
  const months = joinWithY(custom.yearly.months.map(monthLabel));
  if (custom.yearly.useOrdinalWeekday) {
    return `Se repetirá el ${describeOrdinalRule(custom.yearly.ordinal)} de ${months} ${describeIntervalSuffix(interval, 'año', 'años')}.`;
  }
  const monthsPhrase =
    custom.yearly.months.length === 2
      ? `entre ${monthLabel(custom.yearly.months[0])} y ${monthLabel(custom.yearly.months[1])}`
      : `en ${months}`;
  return `Se repetirá ${monthsPhrase} ${describeIntervalSuffix(interval, 'año', 'años')}.`;
}

/** Human-readable sentence for a `RepeatPreset`, mirroring what {@link buildCronExpression} encodes. */
export function describeRepeatPreset(
  preset: RepeatPreset,
  custom: CustomRecurrenceFields,
  startDate: Date,
): string {
  const dayOfMonth = startDate.getDate();
  const month = monthLabel(startDate.getMonth() + 1);
  const weekday = weekdayLabel(
    ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'][startDate.getDay()],
  ).toLowerCase();

  switch (preset) {
    case 'never':
      return 'So se repetirá.';
    case 'every-15-minutes':
      return 'Se repetirá cada 15 minutos.';
    case 'every-30-minutes':
      return 'Se repetirá cada 30 minutos.';
    case 'hourly':
      return 'Se repetirá cada hora.';
    case 'daily':
      return 'Se repetirá cada día.';
    case 'weekdays':
      return 'Se repetirá de lunes a viernes.';
    case 'weekends':
      return 'Se repetirá los fines de semana.';
    case 'weekly':
      return `Se repetirá cada semana el ${weekday}.`;
    case 'monthly':
      return `Se repetirá cada mes el día ${dayOfMonth}.`;
    case 'every-3-months':
      return `Se repetirá cada 3 meses el día ${dayOfMonth}.`;
    case 'every-6-months':
      return `Se repetirá cada 6 meses el día ${dayOfMonth}.`;
    case 'yearly':
      return `Se repetirá cada año el ${dayOfMonth} de ${month}.`;
    case 'custom':
      return describeCustomRecurrence(custom);
    default:
      return 'Se repetirá.';
  }
}

/** Label for the "Repetir" select, e.g. "Cada semana". */
export function repeatPresetLabel(preset: RepeatPreset): string {
  return REPEAT_PRESET_OPTIONS.find((option) => option.value === preset)?.label ?? preset;
}

/** Describes a raw cron expression, falling back to the expression itself when unparseable. */
export function describeCronExpression(expression: string, startDate: Date): string {
  const parsed = parseCronExpression(expression, startDate);
  return parsed
    ? describeRepeatPreset(parsed.preset, parsed.custom, startDate)
    : `Expresión personalizada: ${expression}`;
}
